"use client";

import { useEffect, useMemo, useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  JargonLegend,
  JargonView,
} from "@/components/tools/study-more/jargon-view";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  EMPTY_NOTES,
  explanationStats,
  type FeynmanNotes,
  findJargon,
  notesText,
  parseSavedNotes,
  parseSourceTerms,
  summariseJargon,
} from "@/lib/tool-kit/study-more/feynman";

const STORAGE_KEY = "citationeasy-feynman-technique-v1";

/** English strings. A translated content file overrides these through `ui`. */
export const FEYNMAN_TECHNIQUE_TEMPLATE_STRINGS = {
  heading: "Your Feynman technique notes",
  step1: "1. Choose a concept",
  conceptLabel: "Concept",
  conceptPlaceholder: "e.g. Osmosis",
  sourceLabel: "Where you are learning it from (optional)",
  sourcePlaceholder: "e.g. Biology textbook, chapter 3",
  step2: "2. Explain it as if to a beginner",
  explainLabel: "Your explanation, written from memory",
  explainHint:
    "Imagine a curious 12-year-old with no background. Use short sentences and everyday words. Close the book first: the point is to find out what you can say without it.",
  explainPlaceholder: "Osmosis is when water moves…",
  statWords: "Words",
  statSentences: "Sentences",
  statAverage: "Words per sentence",
  statGrade: "Reading grade (US)",
  gradeNote:
    "The grade is the Flesch–Kincaid formula: a rough guide to sentence and word length for English, not a measure of whether you are right. A 12-year-old is around grade 6 or 7.",
  gradeNeedsMore: "Write at least ten words to see a grade.",
  step3: "3. Find the gaps",
  sourceTermsLabel: "Terms from your source that you can't replace (optional)",
  sourceTermsHint:
    "One per line. Any of them that turn up in your explanation are flagged: ask whether you could say the idea without the term.",
  longWordsLabel: "Also flag long words",
  longWords: { "3": "3 or more syllables", "4": "4 or more syllables" },
  markedHeading: "Your explanation, with flagged words marked",
  markedLabel: "Explanation with flagged words marked",
  kindSource: "Term from your source",
  kindPlain: "Plainer word available",
  kindLong: "Long word",
  flaggedHeading: "Words to look at",
  flaggedCount: "one:{count} time|other:{count} times",
  alternatives: "Try: {options}",
  markFine: "Mark “{word}” as fine",
  markFineButton: "It's fine",
  noFlags:
    "Nothing flagged. That does not prove the explanation is simple: read it aloud, or to a friend, to check.",
  emptyExplanation:
    "Write your explanation above, then the check appears here.",
  gapsLabel: "Where you got stuck, hand-waved or guessed",
  gapsHint:
    "Each one is something to go back to the source for. Be specific: “why does the water move?” beats “osmosis”.",
  missedLabel: "What the source says that your explanation missed",
  missedHint: "Fill this in after you re-read the source.",
  step4: "4. Refine and simplify",
  simpleLabel: "Your second draft: shorter, with an analogy or example",
  simpleHint:
    "Rewrite using only words you have checked. Try an analogy from everyday life, then read it aloud.",
  copyText: "Copy notes",
  copied: "Copied!",
  downloadTxt: "Download .txt",
  txtFileName: "feynman-notes.txt",
  clearNotes: "Clear notes",
  confirmClear: "Click again to clear everything",
  exportLabels: {
    concept: "Concept",
    source: "Source",
    explanation: "My explanation",
    gaps: "Gaps",
    missed: "What I missed",
    simple: "Second draft",
  },
  storageNote:
    "Honest templates, no AI. Your notes are saved in this browser's local storage and nothing is uploaded; they stay on this device and clearing site data erases them.",
  storageUnavailable:
    "This browser is not letting the page save data, so your notes will be gone when you close the page. Copy or download them first.",
} as const;

export default function FeynmanTechniqueTemplateWidget() {
  const strings = useToolUi(FEYNMAN_TECHNIQUE_TEMPLATE_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [notes, setNotes] = useState<FeynmanNotes>(EMPTY_NOTES);
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [minSyllables, setMinSyllables] = useState<"3" | "4">("3");
  const [fine, setFine] = useState<string[]>([]);
  const [clearArmed, setClearArmed] = useState(false);

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      setCanSave(false);
    }
    setNotes(parseSavedNotes(raw));
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
      } catch {
        setCanSave(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [loaded, canSave, notes]);

  const set = (key: keyof FeynmanNotes) => (value: string) =>
    setNotes((current) => ({ ...current, [key]: value }));

  const stats = useMemo(
    () => explanationStats(notes.explanation),
    [notes.explanation]
  );
  const spans = useMemo(
    () =>
      findJargon(notes.explanation, {
        sourceTerms: parseSourceTerms(notes.sourceTerms),
        minSyllables: minSyllables === "4" ? 4 : 3,
        keep: new Set(fine),
      }),
    [notes.explanation, notes.sourceTerms, minSyllables, fine]
  );
  const flagged = summariseJargon(notes.explanation, spans);
  const kindLabels = {
    source: strings.kindSource,
    plain: strings.kindPlain,
    long: strings.kindLong,
  };
  const exportText = notesText(notes, strings.exportLabels);

  function clearAll() {
    if (!clearArmed) {
      setClearArmed(true);
      return;
    }
    setNotes(EMPTY_NOTES);
    setFine([]);
    setClearArmed(false);
  }

  return (
    <div className="space-y-8">
      <h2 className={WIDGET_H2}>{strings.heading}</h2>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.step1}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="fy-concept"
            label={strings.conceptLabel}
            onChange={set("concept")}
            placeholder={strings.conceptPlaceholder}
            value={notes.concept}
          />
          <TextField
            id="fy-source"
            label={strings.sourceLabel}
            onChange={set("source")}
            placeholder={strings.sourcePlaceholder}
            value={notes.source}
          />
        </div>
      </section>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.step2}</h3>
        <TextAreaField
          hint={strings.explainHint}
          id="fy-explain"
          label={strings.explainLabel}
          onChange={set("explanation")}
          placeholder={strings.explainPlaceholder}
          prose
          rows={8}
          value={notes.explanation}
        />
        <div aria-live="polite" className="space-y-2">
          <StatGrid>
            <StatCard label={strings.statWords} value={fmt(stats.words)} />
            <StatCard
              label={strings.statSentences}
              value={fmt(stats.sentences)}
            />
            <StatCard
              label={strings.statAverage}
              value={fmt(stats.averageSentence, { maximumFractionDigits: 1 })}
            />
            <StatCard
              emphasis
              label={strings.statGrade}
              value={
                stats.grade === null
                  ? "–"
                  : fmt(stats.grade, { maximumFractionDigits: 1 })
              }
            />
          </StatGrid>
          <p className={HINT}>
            {stats.grade === null ? strings.gradeNeedsMore : strings.gradeNote}
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.step3}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaField
            hint={strings.sourceTermsHint}
            id="fy-terms"
            label={strings.sourceTermsLabel}
            onChange={set("sourceTerms")}
            prose
            rows={3}
            value={notes.sourceTerms}
          />
          <SelectField
            id="fy-syllables"
            label={strings.longWordsLabel}
            onChange={setMinSyllables}
            options={(["3", "4"] as const).map((value) => ({
              value,
              label: strings.longWords[value],
            }))}
            value={minSyllables}
          />
        </div>
        {notes.explanation.trim() === "" ? (
          <Notice>{strings.emptyExplanation}</Notice>
        ) : (
          <div aria-live="polite" className="space-y-3">
            <p className="font-medium text-foreground text-sm">
              {strings.markedHeading}
            </p>
            <JargonView
              kindLabels={kindLabels}
              label={strings.markedLabel}
              spans={spans}
              text={notes.explanation}
            />
            <JargonLegend labels={kindLabels} />
            {flagged.length === 0 ? (
              <Notice>{strings.noFlags}</Notice>
            ) : (
              <div className={`${CARD} space-y-2`}>
                <p className="font-medium text-foreground text-sm">
                  {strings.flaggedHeading}
                </p>
                <ul className="space-y-2">
                  {flagged.map((item) => (
                    <li
                      className="flex flex-wrap items-center gap-2 text-sm"
                      key={`${item.kind}-${item.word.toLowerCase()}`}
                    >
                      <span className="font-medium text-foreground">
                        {item.word}
                      </span>
                      <span className="text-muted-foreground">
                        {kindLabels[item.kind]} ·{" "}
                        {tPlural(strings.flaggedCount, locale, item.count, {
                          count: fmt(item.count),
                        })}
                      </span>
                      {item.alternatives.length > 0 ? (
                        <span className="text-foreground">
                          {t(strings.alternatives, {
                            options: item.alternatives.join(", "),
                          })}
                        </span>
                      ) : null}
                      {item.kind === "source" ? null : (
                        <button
                          aria-label={t(strings.markFine, { word: item.word })}
                          className={BUTTON_SMALL}
                          onClick={() =>
                            setFine((current) => [
                              ...current,
                              item.word.toLowerCase(),
                            ])
                          }
                          type="button"
                        >
                          {strings.markFineButton}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <TextAreaField
          hint={strings.gapsHint}
          id="fy-gaps"
          label={strings.gapsLabel}
          onChange={set("gaps")}
          prose
          rows={4}
          value={notes.gaps}
        />
        <TextAreaField
          hint={strings.missedHint}
          id="fy-missed"
          label={strings.missedLabel}
          onChange={set("missed")}
          prose
          rows={4}
          value={notes.missed}
        />
      </section>

      <section className="space-y-4">
        <h3 className={WIDGET_H3}>{strings.step4}</h3>
        <TextAreaField
          hint={strings.simpleHint}
          id="fy-simple"
          label={strings.simpleLabel}
          onChange={set("simple")}
          prose
          rows={6}
          value={notes.simple}
        />
      </section>

      <section className={`${CARD} space-y-3`}>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyText}
            text={exportText}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={exportText === ""}
            onClick={() => downloadText(strings.txtFileName, exportText)}
            type="button"
          >
            {strings.downloadTxt}
          </button>
          <button className={BUTTON_SMALL} onClick={clearAll} type="button">
            {clearArmed ? strings.confirmClear : strings.clearNotes}
          </button>
        </div>
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
      </section>
    </div>
  );
}
